import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Check, ArrowRight, Phone } from 'lucide-react';

const WHATSAPP_NUMBER = '+919876543210';

const SERVICES = [
  {
    id: 'shoe-cleaning',
    title: 'Shoe Cleaning',
    description: 'Professional deep cleaning for all types of shoes. We restore leather, suede, and fabric shoes to their original glory using premium cleaning solutions and techniques.',
    price_from: 499,
    image: 'https://images.pexels.com/photos/34510831/pexels-photo-34510831.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    features: ['Deep cleaning', 'Stain removal', 'Odor treatment', 'Leather conditioning', 'Polishing finish'],
  },
  {
    id: 'shoe-repair',
    title: 'Shoe Repair',
    description: 'Expert repair services including sole replacement, heel repair, and leather restoration. Our skilled craftsmen bring damaged shoes back to life.',
    price_from: 799,
    image: 'https://images.unsplash.com/photo-1768550005921-8782adcb798c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxzaG9lJTIwY3JhZnRzbWFufGVufDB8fHx8MTc3NDY1NjA0N3ww&ixlib=rb-4.1.0&q=85',
    features: ['Sole replacement', 'Heel repair', 'Stitching repair', 'Color restoration', 'Hardware fix'],
  },
  {
    id: 'sneaker-restoration',
    title: 'Sneaker Restoration',
    description: 'Specialized restoration for premium sneakers. From deep cleaning to sole restoration and color touch-ups, we handle all types of sneakers with expert care.',
    price_from: 699,
    image: 'https://images.pexels.com/photos/6965875/pexels-photo-6965875.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    features: ['Deep cleaning', 'Sole whitening', 'Midsole restoration', 'Color restoration', 'Protection coating'],
  },
  {
    id: 'handbag-cleaning',
    title: 'Handbag Cleaning & Repair',
    description: 'Premium care for luxury handbags. We handle Chanel, Louis Vuitton, Gucci, and all designer brands with expert precision and care.',
    price_from: 999,
    image: 'https://images.unsplash.com/photo-1598099947145-e85739e7ca28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBsZWF0aGVyJTIwaGFuZGJhZ3xlbnwwfHx8fDE3NzQ2NTYwNDV8MA&ixlib=rb-4.1.0&q=85',
    features: ['Leather conditioning', 'Hardware polishing', 'Stain removal', 'Color restoration', 'Lining repair'],
  },
];

const FadeInSection = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
};

const Services = () => {
  const getWhatsAppLink = (serviceName) => {
    const message = `Hi! I would like to inquire about your ${serviceName} service.`;
    return `https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}?text=${encodeURIComponent(message)}`;
  };

  return (
    <div data-testid="services-page" className="pt-20">
      {/* Hero */}
      <section className="section-padding bg-[#F5F0E6]">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-body text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
              Our Services
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1A1A1A] mb-6">
              Premium Cleaning & Restoration
            </h1>
            <p className="font-body text-base md:text-lg text-[#66635B] max-w-2xl mx-auto">
              Expert care for your luxury shoes and handbags. Each item is treated with the attention and precision it deserves.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto space-y-24">
          {SERVICES.map((service, index) => (
            <FadeInSection key={service.id} delay={0.1}>
              <div
                data-testid={`service-detail-${service.id}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Image */}
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="img-hover aspect-[4/3] overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <span className="font-body text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
                    Service {String(index + 1).padStart(2, '0')}
                  </span>
                  <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A1A1A] mb-4">
                    {service.title}
                  </h2>
                  <p className="font-body text-base text-[#66635B] leading-relaxed mb-6">
                    {service.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3">
                        <div className="w-5 h-5 border border-[#C5A059] flex items-center justify-center">
                          <Check size={12} className="text-[#C5A059]" />
                        </div>
                        <span className="font-body text-sm text-[#1A1A1A]">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Price & CTA */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                    <div>
                      <span className="font-body text-xs text-[#66635B] uppercase tracking-wider">Starting from</span>
                      <p className="font-heading text-3xl font-semibold text-[#1A1A1A]">
                        ₹{service.price_from}
                      </p>
                    </div>
                    <a
                      href={getWhatsAppLink(service.title)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`book-${service.id}`}
                      className="btn-primary flex items-center gap-2"
                    >
                      <Phone size={16} />
                      Book This Service
                    </a>
                  </div>
                </div>
              </div>
            </FadeInSection>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto text-center">
          <FadeInSection>
            <span className="font-body text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
              Custom Requirements?
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-6">
              Need a Custom Quote?
            </h2>
            <p className="font-body text-base text-white/60 mb-10 max-w-xl mx-auto">
              Have special items or requirements? Contact us for a personalized assessment and quote.
            </p>
            <Link
              to="/contact"
              data-testid="services-contact-cta"
              className="btn-secondary inline-flex items-center gap-2 border-white/30 text-white hover:bg-white hover:text-[#1A1A1A]"
            >
              Get Custom Quote
              <ArrowRight size={16} />
            </Link>
          </FadeInSection>
        </div>
      </section>
    </div>
  );
};

export default Services;
